import type { PostHog } from "posthog-js";
import { getCookieConsent } from "./cookieConsent";

interface TesterReplay {
  userId: string;
  distinctId: string;
  campaignId: string;
  tier: string;
  endsAt: string;
  allowed: boolean;
}
let testerReplay: TesterReplay | undefined;
let replayTimer: ReturnType<typeof setTimeout> | undefined;
let recordingFor: string | undefined;
function syncReplay() {
  clearTimeout(replayTimer);
  const remaining = testerReplay
    ? Date.parse(testerReplay.endsAt) - Date.now()
    : 0;
  const eligible =
    getCookieConsent()?.replay &&
    getCookieConsent()?.analytics &&
    testerReplay?.allowed &&
    identity?.id === testerReplay.distinctId &&
    remaining > 0;
  if (!eligible) {
    if (recordingFor) sdk?.stopSessionRecording();
    recordingFor = undefined;
    return;
  }
  if (sdk && recordingFor !== testerReplay!.userId) {
    sdk.stopSessionRecording();
    sdk.register({
      tester_campaign: testerReplay!.campaignId,
      tester_tier: testerReplay!.tier,
    });
    sdk.startSessionRecording({ sampling: true });
    recordingFor = testerReplay!.userId;
  }
  replayTimer = setTimeout(syncReplay, Math.min(remaining, 2_147_483_647));
}

let sdk: PostHog | undefined;
let loading: Promise<PostHog | undefined> | undefined;
let identityRevision = 0;
let identity: { id: string; properties?: Record<string, unknown> } | undefined;
const listeners = new Set<() => void>();
const registrations: Record<string, unknown> = {};

async function load(): Promise<PostHog | undefined> {
  if (!import.meta.env.VITE_POSTHOG_KEY || !getCookieConsent()?.analytics)
    return undefined;
  if (sdk) return sdk;
  if (!loading)
    loading = import("posthog-js")
      .then(({ default: client }) => {
        if (!getCookieConsent()?.analytics) return undefined;
        client.init(import.meta.env.VITE_POSTHOG_KEY, {
          api_host:
            import.meta.env.VITE_POSTHOG_HOST ||
            window.location.origin + "/api/ingest",
          ui_host: "https://us.posthog.com",
          person_profiles: "identified_only",
          opt_out_capturing_by_default: true,
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: { capture_copied_text: false },
          capture_exceptions: true,
          // Autocapture is separate from replay masking. Student-created button
          // labels, document names and element attributes must not become events.
          mask_all_text: true,
          mask_all_element_attributes: true,
          disable_session_recording: true,
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: "*",
            blockSelector:
              'img, canvas, video, iframe, object, embed, [contenteditable="true"], [data-replay-private], a[download], a[href^="blob:"], a[href^="data:"], a[href*="token="], a[href*="/storage/v1/"]',
          },
          enable_recording_console_log: false,
          loaded: (instance) => {
            if (getCookieConsent()?.analytics) instance.opt_in_capturing();
          },
        });
        sdk = client;
        client.register(registrations);
        if (identity) client.identify(identity.id, identity.properties);
        syncReplay();
        client.onFeatureFlags(() =>
          listeners.forEach((listener) => listener()),
        );
        listeners.forEach((listener) => listener());
        return client;
      })
      .catch(() => undefined)
      .finally(() => {
        loading = undefined;
      });
  return loading;
}

/** Small consent-aware facade keeps the analytics SDK off the initial bundle. */
export const posthog = {
  capture(...args: Parameters<PostHog["capture"]>) {
    const revision = identityRevision;
    if (getCookieConsent()?.analytics)
      void load().then((client) => {
        if (revision === identityRevision && getCookieConsent()?.analytics)
          client?.capture(...args);
      });
  },
  identify(id: string, properties?: Record<string, unknown>) {
    if (identity?.id !== id) identityRevision++;
    identity = { id, properties };
    if (!getCookieConsent()?.analytics) return;
    if (sdk) {
      sdk.identify(id, properties);
      syncReplay();
    } else void load();
  },
  reset() {
    identityRevision++;
    testerReplay = undefined;
    identity = undefined;
    syncReplay();
    sdk?.reset();
    delete registrations.tester_campaign;
    delete registrations.tester_tier;
  },
  configureTesterReplay(context?: TesterReplay) {
    if (testerReplay?.userId !== context?.userId) {
      sdk?.stopSessionRecording();
      recordingFor = undefined;
    }
    testerReplay = context;
    if (!context) {
      delete registrations.tester_campaign;
      delete registrations.tester_tier;
      sdk?.unregister?.("tester_campaign");
      sdk?.unregister?.("tester_tier");
    } else {
      Object.assign(registrations, {
        tester_campaign: context.campaignId,
        tester_tier: context.tier,
      });
      if (getCookieConsent()?.analytics) sdk?.register(registrations);
    }
    syncReplay();
    if (context && getCookieConsent()?.replay) void load().then(syncReplay);
  },
  register(properties: Record<string, unknown>) {
    Object.assign(registrations, properties);
    if (getCookieConsent()?.analytics) sdk?.register(properties);
  },
  setPersonProperties(...args: Parameters<PostHog["setPersonProperties"]>) {
    const revision = identityRevision;
    if (getCookieConsent()?.analytics)
      void load().then((client) => {
        if (revision === identityRevision && getCookieConsent()?.analytics)
          client?.setPersonProperties(...args);
      });
  },
  getFeatureFlag(key: string) {
    return sdk?.getFeatureFlag(key);
  },
  onFeatureFlags(listener: () => void) {
    listeners.add(listener);
    if (sdk) listener();
    else void load();
    return () => {
      listeners.delete(listener);
    };
  },
  opt_in_capturing() {
    void load().then((client) => {
      if (!getCookieConsent()?.analytics || !client) return;
      client.opt_in_capturing();
      client.register(registrations);
      if (identity) client.identify(identity.id, identity.properties);
      syncReplay();
    });
  },
  opt_out_capturing() {
    syncReplay();
    sdk?.stopSessionRecording();
    sdk?.opt_out_capturing();
  },
};
if (typeof window !== "undefined" && getCookieConsent()?.analytics) {
  if ("requestIdleCallback" in window)
    window.requestIdleCallback(
      () => {
        void load();
      },
      { timeout: 2000 },
    );
  else
    setTimeout(() => {
      void load();
    }, 0);
}

if (typeof window !== "undefined") {
  window.addEventListener?.("novari:cookie-consent", () => {
    syncReplay();
    if (getCookieConsent()?.analytics) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  });
  window.addEventListener?.("storage", (event) => {
    if (event.key === null || event.key === "novari_cookie_consent_v1") {
      syncReplay();
      if (!getCookieConsent()?.analytics) posthog.opt_out_capturing();
    }
  });
}
